import { Component, HostListener, Inject, OnDestroy, OnInit, ChangeDetectorRef } from "@angular/core";
import { Overlay, ScrollStrategy } from "@angular/cdk/overlay";
import { MatSelect, MAT_SELECT_SCROLL_STRATEGY } from "@angular/material/select";
import { RoutingMatSelectOverlayHelper } from "src/app/routing-ruleconfig1/utils/routing-mat-select-overlay.util";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  getRuleNameValidationMessage,
  isDuplicateRuleName,
  RULE_NAME_ALREADY_EXISTS_MESSAGE,
  ruleNameFormatValidator,
  RuleEngineService,
} from "src/app/services/rule-engine.service";
import { ToastrService } from "ngx-toastr";
import {
  ConditionGroup,
  NestedGroups,
  PredicateData,
  REMethodNames,
  REMethodParameters,
  ReturnResponse,
  Rule,
} from "../models/rule-engine";
import { DataParserService } from "src/app/services/data-parser.service";
import { CommonService } from "src/app/services/common.service";
import { map, Observable, startWith, Subscription } from "rxjs";
import { ShowEditButtonPipe } from "../show-edit-button.pipe";

export function customRulesMatSelectScrollStrategyFactory(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

/**
 * ADDED: Date / date+time patterns (engine-supported). Used when XPath leaf ends with Date
 * (e.g. @CollectionDate) — time-only is NOT allowed for those XPaths.
 */
const DATE_OR_DATETIME_MATCH_PATTERNS: RegExp[] = [
  /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/,
  /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}\s+([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/,
  /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}\s+(0?[1-9]|1[0-2]):[0-5]\d(:[0-5]\d)?\s*[AaPp][Mm]$/,
  /^\d{4}-\d{2}-\d{2}$/,
  /^\d{4}-\d{2}-\d{2}[ T]([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d{1,7})?)?(Z|[+-]\d{2}:?\d{2})?$/,
  /^\d{4}-\d{2}-\d{2}\s+(0?[1-9]|1[0-2]):[0-5]\d(:[0-5]\d)?\s*[AaPp][Mm]$/,
];

/**
 * ADDED: Time-only patterns (engine-supported). Used when XPath leaf ends with Time
 * (e.g. @CollectionTime) — date / date+time is NOT allowed for those XPaths.
 */
const TIME_ONLY_MATCH_PATTERNS: RegExp[] = [
  /^([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/,
  /^(0?[1-9]|1[0-2]):[0-5]\d(:[0-5]\d)?\s*[AaPp][Mm]$/,
];

// EXISTING (commented): single list that allowed time-only even for @...Date XPaths.
// const DATE_TO_MATCH_FORMAT_PATTERNS: RegExp[] = [
//   ...DATE_OR_DATETIME_MATCH_PATTERNS,
//   ...TIME_ONLY_MATCH_PATTERNS,
// ];

/** ADDED: Classifies Date Locator leaf — @CollectionDate → date, @CollectionTime → time. */
function getDateLocatorLeafKind(
  xpath: string
): "date" | "time" | "other" {
  const leaf = (xpath ?? "").trim().split("/").pop() ?? "";
  const name = leaf.replace(/^@/, "");
  if (/date$/i.test(name)) {
    return "date";
  }
  if (/time$/i.test(name)) {
    return "time";
  }
  return "other";
}

/**
 * ADDED: DateToMatch format validator — rules depend on selected Date Locator XPath:
 * - @...Date → date or date+time required (reject time-only)
 * - @...Time → time-only required
 * - other → any previously supported format
 * Empty → required validator handles.
 */
function dateToMatchFormatValidator(
  getXPath: () => string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value == null ? "" : String(control.value)).trim();
    if (!raw) {
      return null;
    }
    // ADDED: HL7 Date Locator — allow free text (no date/time format check).
    const xpath = (getXPath() ?? "").trim();
    if (/^hl7message\//i.test(xpath) || xpath.includes("[@parentId")) {
      return null;
    }
    const kind = getDateLocatorLeafKind(getXPath());
    let patterns: RegExp[];
    if (kind === "date") {
      patterns = DATE_OR_DATETIME_MATCH_PATTERNS;
    } else if (kind === "time") {
      patterns = TIME_ONLY_MATCH_PATTERNS;
    } else {
      patterns = DATE_OR_DATETIME_MATCH_PATTERNS.concat(
        TIME_ONLY_MATCH_PATTERNS
      );
    }
    const isSupported = patterns.some((pattern) => pattern.test(raw));
    return isSupported ? null : { dateToMatchFormat: true };
  };
}

@Component({
  selector: "app-custom-rules",
  templateUrl: "./custom-rules.component.html",
  styleUrls: ["./custom-rules.component.scss"],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: customRulesMatSelectScrollStrategyFactory,
    },
  ],
})
export class CustomRulesComponent implements OnInit, OnDestroy {
  private readonly matSelectOverlay = new RoutingMatSelectOverlayHelper();
  public methodNames: string[] = [];
  public methodParametersData: any[] = [];
  public customRules: FormGroup;
  public selectedMethodName = new FormControl("", Validators.required);
  public apNodePaths: string[] = [];
  public cpNodePaths: string[] = [];
  public allNodePaths: string[] = [];
  filteredOptionsMap: Map<number, Observable<string[]>> = new Map();
  parameterDropdownOptionsMap: Map<number, string[]> = new Map();
  private lookupOptionsLoadingIndices = new Set<number>();
  private routeUniqueID: any;
  private openPredicateFilter: boolean = false;
  public xPathsWithLeafNodes: any;
  private defaultLogicalOperatorForSimpleFilter: string = "AND";
  private indexOfTheFieldToUpdateThePath: number;
  private rules: Rule[] = [];
  public rulename = new FormControl("", [
    Validators.required,
    ruleNameFormatValidator,
  ]);
  private _rulesSubjectSubscription!: Subscription;
  private _updateFieldWithPredicateConditionsSubscription!: Subscription;
  private _nodesWithTheirLeafNodesSubscription!: Subscription;
  public  _isEdit = false;
  private _finalRuleExpression: string = "";
  // ADDED: Blocks method selectionChange from wiping prefilled edit values.
  private _suppressMethodSelectionChange = false;
  // ADDED: Display order of FormArray indices (ValidateDate: XPath, Operator, DateToMatch).
  // generateParam still joins controls in index order 0|1|2 = xpath|dateToMatch|operation.
  public parameterDisplayIndices: number[] = [];
  // ADDED: Re-validate DateToMatch when Date Locator XPath changes (@...Date vs @...Time).
  private _dateToMatchXPathSubscription: Subscription | null = null;
  // ADDED: Inline DateToMatch format help (dialog matTooltip flickered / was unreadable).
  public dateToMatchHelpVisible = false;
  public dateToMatchHelpPinned = false;
  private _dateToMatchHelpHideTimer: ReturnType<typeof setTimeout> | null = null;
  // EXISTING (commented): date/time picker FormControls — DateToMatch is free text again.
  // public dateToMatchDate = new FormControl<string>("");
  // public dateToMatchTime = new FormControl<string>("");
  // public dateToMatchTimeMode = new FormControl<"24h" | "12h">("24h");
  // public dateToMatchHour = new FormControl<string>("");
  // public dateToMatchMinute = new FormControl<string>("");
  // public dateToMatchSecond = new FormControl<string>("");
  // public dateToMatchAmPm = new FormControl<"AM" | "PM">("AM");
  // public readonly dateToMatchHours24: string[] = ...
  // public readonly dateToMatchHours12: string[] = ...
  // public readonly dateToMatchMinuteSecondOptions: string[] = ...

  constructor(
    public dialogRef: MatDialogRef<CustomRulesComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      rule?: Rule;
      finalRuleExpression?: string;
      routeUniqueID?: any;
    },
    private _fb: FormBuilder,
    private ruleEngineService: RuleEngineService,
    private toastr: ToastrService,
    private _dataParserService: DataParserService,
    private _ruleEngineService: RuleEngineService,
    private showEditButtonPipe: ShowEditButtonPipe,
    private _commonService: CommonService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.customRules = this._fb.group({
      parameters: this._fb.array([]),
    });

    const incomingRule = this.dialogData?.rule;
    this._finalRuleExpression = this.dialogData?.finalRuleExpression ?? "";
    this.routeUniqueID = this.dialogData?.routeUniqueID;

    if (incomingRule) {
      this._isEdit = true;
      let param = incomingRule.param;
      let paramsArray = param.split("|");
      let methodName = incomingRule.field;

      // ADDED: Avoid selectionChange clearing params while edit prefills load.
      this._suppressMethodSelectionChange = true;
      // Set the method name and rule name
      this.selectedMethodName.setValue(methodName, { emitEvent: false });
      this.rulename.setValue(incomingRule.rulename);

      // Same behavior as create-rules: lock rule name when used in build expression.
      const disableRuleName = !this.showEditButtonPipe.transform(
        incomingRule.rulename,
        this._finalRuleExpression
      );
      if (disableRuleName) {
        this.rulename.disable({ emitEvent: false });
      } else {
        this.rulename.enable({ emitEvent: false });
      }

      this._ruleEngineService.getMethodParameters(methodName).subscribe({
        next: (response: ReturnResponse<REMethodParameters[]>) => {
          this.applyMethodParametersResponse(
            response.responsebody ?? [],
            paramsArray
          );
          this._suppressMethodSelectionChange = false;
        },
        error: () => {
          this._suppressMethodSelectionChange = false;
          this.toastr.error("Unable to get parameters", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
    }

    this.ruleEngineService.getMethodNames().subscribe({
      next: (response: ReturnResponse<REMethodNames>) => {
        this.methodNames = response.responsebody.MethodNames;
      },
      error: (err) => {
        this.toastr.error("Unable to get method names", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });

    this._nodesWithTheirLeafNodesSubscription =
      this._dataParserService.$getNodesWithTheirLeafNodes.subscribe({
        next: (data) => (this.xPathsWithLeafNodes = data),
        error: (err) => {
          this.toastr.error("Unable to get leaf nodes", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    this._updateFieldWithPredicateConditionsSubscription =
      this._ruleEngineService.$getUpdatedFieldWithPredicate.subscribe({
        next: (data) => {
          // Only set value if the control exists and index is valid
          if (
            this.parameters?.controls &&
            this.indexOfTheFieldToUpdateThePath !== undefined &&
            this.parameters.controls[this.indexOfTheFieldToUpdateThePath]
          ) {
            this.parameters.controls[
              this.indexOfTheFieldToUpdateThePath
            ].setValue(data);
          }
        },
        error: (err) => {
          this.toastr.error("Unable to get field", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    this._rulesSubjectSubscription = this._ruleEngineService
      .getAllRules()
      .subscribe({
        next: (data) => (this.rules = data ?? []),
        error: (err) => {
          this.toastr.error("Unable to get rules", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    //load the AP and CP data
    this.apNodePaths = this._dataParserService.APNodePaths;
    this.cpNodePaths = this._dataParserService.CPNodePaths;
    // Use Set to remove duplicates when combining paths
    this.allNodePaths = [...new Set([...this.apNodePaths, ...this.cpNodePaths])];
  }

  ngOnDestroy(): void {
    this.matSelectOverlay.destroy();
    if (this._rulesSubjectSubscription) {
      this._rulesSubjectSubscription.unsubscribe();
    }
    if (this._updateFieldWithPredicateConditionsSubscription) {
      this._updateFieldWithPredicateConditionsSubscription.unsubscribe();
    }
    if (this._nodesWithTheirLeafNodesSubscription) {
      this._nodesWithTheirLeafNodesSubscription.unsubscribe();
    }
    // ADDED: Drop XPath→DateToMatch revalidation subscription.
    this.unsubscribeDateToMatchXPathWatch();
    this.clearDateToMatchHelpHideTimer();
  }

  onClickClose() {
    this.dialogRef.close(false); // Pass false to indicate cancellation
  }

  onMatSelectOpenedChange(opened: boolean, select: MatSelect): void {
    this.matSelectOverlay.onOpenedChange(opened, select);
  }

  private getRuleNameInputValue(): string {
    const raw = this.rulename.disabled
      ? this.rulename.getRawValue()
      : this.rulename.value;
    return (raw ?? "").toString().trim();
  }

  onRuleNameBlur(): void {
    this.rulename.markAsTouched();
    this.showRuleNameValidationToastIfNeeded();
  }

  private showRuleNameValidationToastIfNeeded(): boolean {
    const message = getRuleNameValidationMessage(this.getRuleNameInputValue());
    if (!message) {
      return false;
    }
    this.toastr.warning(message, "", {
      timeOut: 4000,
      positionClass: "toast-bottom-right",
    });
    return true;
  }

  onSubmit() {
    this.selectedMethodName.markAsTouched();
    this.rulename.markAsTouched();
    this.customRules.markAllAsTouched();

    if (this.showRuleNameValidationToastIfNeeded()) {
      return;
    }

    // ADDED: When rule name is locked (used in expression), Angular sets status DISABLED
    // so rulename.valid is false even though the value is present — allow submit in that case.
    const isRuleNameAcceptable = this.rulename.disabled || this.rulename.valid;

    if (
      this.customRules.valid &&
      this.selectedMethodName.valid &&
      isRuleNameAcceptable
    ) {
      if (this._isEdit) {
        const editingRule = this.dialogData?.rule;
        if (!editingRule) return;

        if (
          isDuplicateRuleName(this.rules, this.getRuleNameInputValue(), {
            excludeRuleNumber: editingRule.rulenumber,
          })
        ) {
          this.showDuplicateRuleNameWarning();
        } else {
          // Update the rule
          const updatedRule: Rule = {
            rulenumber: editingRule.rulenumber,
            ruletype: "Method",
            param: this.generateParam(),
            rulename: this.getRuleNameInputValue(),
            operator: "IN",
            value: "true",
            field: this.selectedMethodName.value,
          };

          // Find and update the rule in the rules array
          // ADDED: Number() so string/number rulenumber from API still matches.
          const index = this.rules.findIndex(
            (rule) =>
              Number(rule.rulenumber) === Number(editingRule.rulenumber)
          );
          if (index !== -1) {
            this.rules[index] = updatedRule;
            this._ruleEngineService.setAllTheRules(this.rules);
            // ADDED: Confirm Update to the user (dialog closes after success).
            this.toastr.success("Rule updated successfully", "", {
              timeOut: 3000,
              positionClass: "toast-bottom-right",
            });
            this.dialogRef.close(true); // Pass true to indicate successful save
          }
        }
      } else {
        if (!this.checkEditDupliate()) {
          this.addRule();
          // ADDED: Confirm Create to the user (same pattern as Update).
          this.toastr.success("Rule created successfully", "", {
            timeOut: 3000,
            positionClass: "toast-bottom-right",
          });
          this.dialogRef.close(true); // Pass true to indicate successful save
        }
      }
    } else {
      this.toastr.warning("Please fill all required fields", "", {
        timeOut: 3000,
        positionClass: "toast-bottom-right",
      });
    }
  }
  private addRule() {
    let rule: Rule = {
      rulenumber: this.rules.length + 1,
      ruletype: "Method",
      param: this.generateParam(),
      rulename: this.getRuleNameInputValue(),
      operator: "IN",
      value: "true",
      field: this.selectedMethodName.value,
    };
    this.rules.push(rule);
    this._ruleEngineService.setAllTheRules(this.rules);
  }
  private checkEditDupliate(): boolean {
    if (isDuplicateRuleName(this.rules, this.getRuleNameInputValue())) {
      this.showDuplicateRuleNameWarning();
      return true;
    }
    return false;
  }
  private showDuplicateRuleNameWarning(): void {
    this.toastr.warning(RULE_NAME_ALREADY_EXISTS_MESSAGE, "", {
      timeOut: 4000,
      positionClass: "toast-bottom-right",
    });
  }
  private generateParam(): string {
    // FormArray order remains xpath|dateToMatch|operation (engine contract).
    return this.parameters.controls
      .map((param) => {
        const raw = param.value;
        return (raw == null ? "" : String(raw)).trim();
      })
      .join("|");
  }
  get parameters(): FormArray {
    return this.customRules.get("parameters") as FormArray;
  }

  /**
   * ADDED: True for ValidateDate DateToMatch slot (index 1 in FormArray / param).
   * Used for the format info icon (placeholder/note removed).
   */
  isDateToMatchParameter(index: number): boolean {
    return (
      this.selectedMethodName.value === "ValidateDate" && index === 1
    );
  }

  /**
   * ADDED: Old ValidateDate one-row layout only when ValidateDate params are loaded (length 3).
   * Prevents Lookup fields from briefly getting 3-column CSS while switching methods.
   */
  isValidateDateParamLayout(): boolean {
    return (
      this.selectedMethodName.value === "ValidateDate" &&
      this.methodParametersData.length === 3
    );
  }

  /**
   * ADDED: DateToMatch help rows (display only). Same HL7 path check as
   * dateToMatchFormatValidator — does not change validation or generateParam().
   */
  private readonly dateToMatchXmlHelpRows: { label: string; value: string }[] = [
    { label: "Date", value: "MM/DD/YYYY" },
    { label: "Date+time", value: "MM/DD/YYYY hh:mm\nor MM/DD/YYYY HH:mm" },
    { label: "Time", value: "hh:mm or HH:mm" },
  ];
  private readonly dateToMatchHl7HelpRows: { label: string; value: string }[] = [
    { label: "Date", value: "yyyyMMdd" },
    { label: "Date+time", value: "yyyyMMddHHmm or yyyyMMddHHmmss" },
    { label: "Time", value: "HHmm or HHmmss" },
  ];

  /**
   * ADDED: Date Locator text for help-icon visibility (display only).
   */
  getDateLocatorPath(): string {
    if (!this.customRules || this.parameters.length < 1) {
      return "";
    }
    const xpath = this.parameters.at(0)?.value;
    return (xpath == null ? "" : String(xpath)).trim();
  }

  isHl7DateLocator(): boolean {
    const raw = this.getDateLocatorPath();
    return /^hl7message\//i.test(raw) || raw.includes("[@parentId");
  }

  getDateToMatchFormatHelpSections(): {
    heading: string;
    rows: { label: string; value: string }[];
  }[] {
    const path = this.getDateLocatorPath();
    if (!path) {
      return [];
    }
    return [
      {
        heading: "Allowed Format",
        rows: this.isHl7DateLocator()
          ? this.dateToMatchHl7HelpRows
          : this.dateToMatchXmlHelpRows,
      },
    ];
  }

  showDateToMatchHelp(): void {
    if (!this.getDateLocatorPath()) {
      return;
    }
    this.clearDateToMatchHelpHideTimer();
    this.dateToMatchHelpVisible = true;
  }

  hideDateToMatchHelp(): void {
    if (this.dateToMatchHelpPinned) {
      return;
    }
    this.clearDateToMatchHelpHideTimer();
    this._dateToMatchHelpHideTimer = setTimeout(() => {
      this.dateToMatchHelpVisible = false;
      this._cdr.markForCheck();
    }, 180);
  }

  toggleDateToMatchHelp(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.getDateLocatorPath()) {
      this.dateToMatchHelpPinned = false;
      this.dateToMatchHelpVisible = false;
      return;
    }
    this.clearDateToMatchHelpHideTimer();
    this.dateToMatchHelpPinned = !this.dateToMatchHelpPinned;
    this.dateToMatchHelpVisible = this.dateToMatchHelpPinned;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClickCloseDateToMatchHelp(event: Event): void {
    if (!this.dateToMatchHelpVisible && !this.dateToMatchHelpPinned) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest(".date-to-match-field-wrap")) {
      return;
    }
    this.dateToMatchHelpPinned = false;
    this.dateToMatchHelpVisible = false;
  }

  private clearDateToMatchHelpHideTimer(): void {
    if (this._dateToMatchHelpHideTimer) {
      clearTimeout(this._dateToMatchHelpHideTimer);
      this._dateToMatchHelpHideTimer = null;
    }
  }

  /** True when API Value column lists pipe-separated choices for a mat-select. */
  hasParameterDropdown(index: number): boolean {
    return this.isDropdownParameter(index) && this.getParameterDropdownOptions(index).length > 0;
  }

  isDropdownParameter(index: number): boolean {
    const param = this.methodParametersData[index];
    return this.isConstantParameter(param) || this.isLookupApiParameter(param);
  }

  isLookupApiParameter(index: number): boolean;
  isLookupApiParameter(param: REMethodParameters | undefined): boolean;
  isLookupApiParameter(
    indexOrParam: number | REMethodParameters | undefined
  ): boolean {
    const param =
      typeof indexOrParam === "number"
        ? this.methodParametersData[indexOrParam]
        : indexOrParam;
    if (!param) {
      return false;
    }
    return (
      this.getParameterValueType(param) === "api" &&
      this.getParameterValue(param).toLowerCase() === "lookup"
    );
  }

  isLookupOptionsLoading(index: number): boolean {
    return this.lookupOptionsLoadingIndices.has(index);
  }

  isXPathParameter(index: number): boolean {
    return !!this.methodParametersData[index]?.ShowXPath;
  }

  getParameterDropdownOptions(index: number): string[] {
    // EXISTING (commented): re-parsing Constant options every CD created a new array and
    // could clear mat-select value on Update → false "fill all required fields" toast.
    // if (this.isLookupApiParameter(index)) {
    //   return this.parameterDropdownOptionsMap.get(index) ?? [];
    // }
    // return this.parseConstantDropdownOptions(this.methodParametersData[index]);

    // ADDED: Always use cached options map (lookup + constant) for stable mat-select binding.
    const cached = this.parameterDropdownOptionsMap.get(index);
    if (cached) {
      return cached;
    }
    if (this.isLookupApiParameter(index)) {
      return [];
    }
    return this.parseConstantDropdownOptions(this.methodParametersData[index]);
  }

  private getParameterValueType(param: REMethodParameters): string {
    return (param.ValueType ?? (param as { valueType?: string }).valueType ?? "")
      .toString()
      .trim()
      .toLowerCase();
  }

  private getParameterValue(param: REMethodParameters): string {
    return (param.Value ?? (param as { value?: string }).value ?? "")
      .toString()
      .trim();
  }

  private isConstantParameter(param: REMethodParameters | undefined): boolean {
    if (!param) {
      return false;
    }
    const valueType = this.getParameterValueType(param);
    if (valueType === "constant") {
      return !!this.getParameterValue(param);
    }
    if (!valueType) {
      return this.parseConstantDropdownOptions(param).length > 0;
    }
    return false;
  }

  /**
   * Parses constant parameter Value: supports "||" or "|" delimiters.
   * @example { Value: '>||>=||<=||<||=' } → ['>', '>=', '<=', '<', '=']
   */
  private parseConstantDropdownOptions(
    param: REMethodParameters | undefined
  ): string[] {
    if (!param) {
      return [];
    }
    const raw = this.getParameterValue(param);
    if (!raw) {
      return [];
    }
    const parts = raw.includes("||") ? raw.split("||") : raw.split("|");
    return parts.map((p) => p.trim()).filter(Boolean);
  }

  private async fetchXmlMappings(): Promise<any[]> {
    if (!this.routeUniqueID) {
      return [];
    }
    const interfacemapping = {
      ContextId: this.routeUniqueID,
      Handler: "",
    };
    try {
      const data = await this._commonService.postapi(
        "api/XMLMappings/GetByHandler",
        interfacemapping
      );
      return Array.isArray(data) ? data : [];
    } catch {
      this.toastr.error("Unable to load lookup values", "", {
        timeOut: 4000,
        positionClass: "toast-bottom-right",
      });
      return [];
    }
  }

  private extractLookupDropdownOptions(lookups: any[]): string[] {
    const lookupTypes = lookups
      .map((row) =>
        (row.LookupType ?? row.lookupType ?? "").toString().trim()
      )
      .filter(Boolean);
    return [...new Set(lookupTypes)];
  }

  private async loadLookupDropdownOptions(
    methodParams: REMethodParameters[]
  ): Promise<void> {
    const lookupParams = methodParams
      .map((param, index) => ({ param, index }))
      .filter(({ param }) => this.isLookupApiParameter(param));

    if (!lookupParams.length) {
      return;
    }

    if (!this.routeUniqueID) {
      this.toastr.warning(
        "Unable to load lookup options: route context is missing",
        "",
        {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        }
      );
      return;
    }

    lookupParams.forEach(({ index }) => this.lookupOptionsLoadingIndices.add(index));
    const lookups = await this.fetchXmlMappings();
    lookupParams.forEach(({ index }) => {
      const options = this.extractLookupDropdownOptions(lookups);
      this.parameterDropdownOptionsMap.set(index, options);
      this.lookupOptionsLoadingIndices.delete(index);
    });
    this._cdr.markForCheck();
  }

  private applyMethodParametersResponse(
    methodParams: REMethodParameters[],
    prefilledValues?: string[]
  ): void {
    this.filteredOptionsMap.clear();
    this.parameterDropdownOptionsMap.clear();
    this.lookupOptionsLoadingIndices.clear();
    // EXISTING (commented): UI label overrides — labels now come from DB (REParameters.Label) only.
    // this.applyValidateDateDisplayLabels(methodParams);
    this.methodParametersData = methodParams.map((methodParam) => methodParam);
    this.parameters.clear();

    methodParams.forEach((param, index) => {
      const initial =
        prefilledValues && index < prefilledValues.length
          ? prefilledValues[index]
          : "";
      // ADDED: ValidateDate DateToMatch (index 1) — required + XPath-aware format check.
      // EXISTING (commented): dateToMatchFormatValidator() without XPath (allowed time-only for @...Date).
      // ? [Validators.required, dateToMatchFormatValidator()]
      const validators =
        this.selectedMethodName.value === "ValidateDate" && index === 1
          ? [
              Validators.required,
              dateToMatchFormatValidator(() => {
                const xpathCtrl = this.parameters.at(0);
                const xpathVal = xpathCtrl?.value;
                return xpathVal == null ? "" : String(xpathVal);
              }),
            ]
          : [Validators.required];
      this.parameters.push(this._fb.control(initial, validators));

      if (param.ShowXPath) {
        this.filteredOptionsMap.set(
          index,
          this.parameters.controls[index].valueChanges.pipe(
            startWith(initial),
            map((value) => this._filter(value as string, this.allNodePaths))
          )
        );
      } else if (this.isConstantParameter(param)) {
        // ADDED: Cache Constant options once (stable mat-select binding on Update).
        this.parameterDropdownOptionsMap.set(
          index,
          this.parseConstantDropdownOptions(param)
        );
      }
    });

    // ADDED: When Date Locator changes, re-check DateToMatch against @...Date / @...Time rules.
    this.wireDateToMatchXPathRevalidation();

    // ADDED: Screen order XPath → Operator → DateToMatch; param join order unchanged.
    this.parameterDisplayIndices = this.buildParameterDisplayIndices(
      methodParams.length
    );

    void this.loadLookupDropdownOptions(methodParams);
  }

  /**
   * ADDED: ValidateDate display order (LHS XPath, Operator, RHS DateToMatch).
   * FormArray indices stay 0=xpath, 1=dateToMatch, 2=operation for generateParam().
   */
  private buildParameterDisplayIndices(length: number): number[] {
    if (
      this.selectedMethodName.value === "ValidateDate" &&
      length === 3
    ) {
      return [0, 2, 1];
    }
    const indices: number[] = [];
    for (let i = 0; i < length; i++) {
      indices.push(i);
    }
    return indices;
  }

  /**
   * EXISTING (commented): Previously forced ValidateDate labels in UI.
   * Labels now display from API/DB only (REParameters.Label) — no override; data flow unchanged.
   */
  // private applyValidateDateDisplayLabels(
  //   methodParams: REMethodParameters[]
  // ): void {
  //   if (this.selectedMethodName.value !== "ValidateDate") {
  //     return;
  //   }
  //   methodParams.forEach((param) => {
  //     if (param.ShowXPath) {
  //       param.Label = "Date Locator";
  //       return;
  //     }
  //     if (this.isConstantParameter(param)) {
  //       param.Label = "Operator";
  //     }
  //     // DateToMatch: leave API Label as-is.
  //   });
  // }

  addParameter() {
    this.parameters.push(this._fb.control("", Validators.required));
  }
  onSelectionChange() {
    // ADDED: Skip while edit prefills are loading (avoids wiping xpath/date/operation).
    if (this._suppressMethodSelectionChange) {
      return;
    }
    // ADDED: Clear stale parameter UI immediately on method change
    // (prevents ValidateDate/Lookup layout glitch while API loads).
    this.clearMethodParameterUiState();
    this.ruleEngineService
      .getMethodParameters(this.selectedMethodName.value)
      .subscribe({
        next: (response: ReturnResponse<REMethodParameters[]>) => {
          this.applyMethodParametersResponse(response.responsebody ?? []);
        },
        error: (err) => {
          this.toastr.error("Unable to get parameters", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }

  /**
   * ADDED: Resets FormArray / maps so previous method fields never flash under new method CSS.
   */
  private clearMethodParameterUiState(): void {
    this.unsubscribeDateToMatchXPathWatch();
    this.filteredOptionsMap.clear();
    this.parameterDropdownOptionsMap.clear();
    this.lookupOptionsLoadingIndices.clear();
    this.methodParametersData = [];
    this.parameterDisplayIndices = [];
    this.parameters.clear();
  }

  /**
   * ADDED: Date Locator valueChanges → refresh DateToMatch validity (same FormArray / generateParam).
   */
  private wireDateToMatchXPathRevalidation(): void {
    this.unsubscribeDateToMatchXPathWatch();
    if (
      this.selectedMethodName.value !== "ValidateDate" ||
      this.parameters.length < 2
    ) {
      return;
    }
    this._dateToMatchXPathSubscription = this.parameters
      .at(0)
      .valueChanges.subscribe(() => {
        const dateToMatchCtrl = this.parameters.at(1);
        dateToMatchCtrl.updateValueAndValidity({ emitEvent: false });
        this._cdr.markForCheck();
      });
  }

  /** ADDED: Safe unsubscribe for XPath watch (method change / destroy). */
  private unsubscribeDateToMatchXPathWatch(): void {
    if (this._dateToMatchXPathSubscription) {
      this._dateToMatchXPathSubscription.unsubscribe();
      this._dateToMatchXPathSubscription = null;
    }
  }

  private _filter(value: string, options: string[]): string[] {
    const filterValue = value?.toLowerCase() || "";
    return options.filter((option) =>
      option.toLowerCase().includes(filterValue)
    );
  }

  //#region This region is responsible for opening the predicate filter component
  onDoubleClick(index: number) {
    this.indexOfTheFieldToUpdateThePath = index;
    //this will split the path in the filed form control based on the '/'
    let individualNodes = this.parameters.controls[index].value.split("/");
    //we are storing the last part of the xpath from the user entered field as we need it later to check if the xpath actually exists in the list of all the xpaths we have got from the xml template
    let lastPartOfTheField = individualNodes[individualNodes.length - 1];
    //here will get all the nodes and their filters as key and values
    let kvpOfNodeAndFilters: { [key: string]: string | null }[] =
      this.getKeyValueOfNodeAndFilter(individualNodes);
    //here we will check if the xpath after removing all the filters will match with any of the xpaths that we have generated from the xml template
    let allowEdit = this.isPathMatch(kvpOfNodeAndFilters, lastPartOfTheField);
    if (!allowEdit) {
      this.toastr.warning("The path is not present in AP or CP", "", {
        timeOut: 4000,
        positionClass: "toast-bottom-right",
      });

      //here we are trying to make sure that the predicate filter does not open because there is no matching path in the CP or AP
      this.openPredicateFilter = false;
      this._ruleEngineService.setPopUpValue(this.openPredicateFilter);
    } else {
      let xpathWithoutFilters =
        this.getXPathWithoutFilters(kvpOfNodeAndFilters);
      let xPaths = this.generateXPathsFromField(xpathWithoutFilters);
      let listOfPredicateData = this.getNodesWithLeaves(xPaths);
      let hasFilters = this.hasFilters(kvpOfNodeAndFilters);
      if (hasFilters) {
        let finalPredicateData = this.generateTheDataWhenFilters(
          kvpOfNodeAndFilters,
          listOfPredicateData
        );
        this._ruleEngineService.dataToSendForPredicatePopUp =
          finalPredicateData;
        this._ruleEngineService.setSubConditionsFor(
          xpathWithoutFilters,
          this.xPathsWithLeafNodes,
          this.allNodePaths
        );
      } else {
        let finalPredicateData =
          this.generateTheDataWhenNoFilters(listOfPredicateData);
        this._ruleEngineService.dataToSendForPredicatePopUp =
          finalPredicateData;
        this._ruleEngineService.setSubConditionsFor(
          xpathWithoutFilters,
          this.xPathsWithLeafNodes,
          this.allNodePaths
        );
      }

      //here we are trying to make sure that the predicate filter does open because there is matching path in the CP or AP
      this.openPredicateFilter = true;
      this._ruleEngineService.setPopUpValue(this.openPredicateFilter);
    }
  }
  //this method will take user entered xpath and return all the nodes as key and all the filters as their values
  getKeyValueOfNodeAndFilter(
    individualNodes: string[]
  ): { [key: string]: string | null }[] {
    let kvpOfNodeAndFilters: { [key: string]: string | null }[] = [];
    individualNodes.map((node: any) => {
      let regex = /([^\[]+)(\[[^\]]+\])?/;
      //first checking if the node has any filters by using the regular expression then returning true or false based on that
      let isMatch = regex.test(node);
      //take the first node add the node as key and its filters as value to the kvpOfNodeAndFilters variable
      if (isMatch) {
        const match = node.match(regex);
        if (match[2]) {
          let key = match[1];
          let value = match[2];
          kvpOfNodeAndFilters.push({ [key]: value });
        } else {
          let key = match[1];
          let value = null;
          kvpOfNodeAndFilters.push({ [key]: value });
        }
      }
    });
    return kvpOfNodeAndFilters;
  }

  //this method checks if the given path matches with the xpaths we have generated from xml. returns true if it matches or false
  isPathMatch(
    kvpOfNodeAndFilters: { [key: string]: string | null }[],
    lastPartOfTheField: string
  ): boolean {
    //here we are generating the plain xpath without the filters to compare and them with the xpaths we have generated from the loaded xml
    let xpathWithoutFilters = this.getXPathWithoutFilters(kvpOfNodeAndFilters);
    if (this.allNodePaths.includes(xpathWithoutFilters)) {
      return true;
    } else {
      return false;
    }
  }

  getXPathWithoutFilters(
    kvpOfNodeAndFilters: { [key: string]: string | null }[]
  ): string {
    let xpathWithoutFilters: string = "";
    kvpOfNodeAndFilters.map((node: any, index: number) => {
      xpathWithoutFilters += Object.keys(node)[0];
      if (index !== kvpOfNodeAndFilters.length - 1) {
        xpathWithoutFilters += "/";
      }
    });
    //here we are manually adding the last part of the xpath that we have extracted earlier to finally arrive at the xpath which we need to compare with the xpaths generated form the xml
    xpathWithoutFilters;
    return xpathWithoutFilters;
  }

  //this will generate multiple xpaths from the xpath provided by the user.
  //Eg: Case/OrderingProvider/Location[@AccountType='Ordering Facility']/@OrganizationGUID
  //Output will be array of strings of these values: "Case", "Case/OrderingProvider", "Case/OrderingProvider/Location"
  generateXPathsFromField(xPathsWithoutFilters: string): string[] {
    let xPathWithOnlyParentNodes = xPathsWithoutFilters
      ?.split("/")
      .slice(0, -1);
    if (
      !xPathWithOnlyParentNodes ||
      !Array.isArray(xPathWithOnlyParentNodes) ||
      xPathWithOnlyParentNodes.length === 0
    ) {
      return []; // Return an empty array if input is invalid
    }

    let xPaths: string[] = [];
    let currentPath = "";

    for (const path of xPathWithOnlyParentNodes) {
      currentPath = currentPath ? `${currentPath}/${path}` : path;
      xPaths.push(currentPath);
    }
    return xPaths;
  }
  getNodesWithLeaves(xPaths: string[]): PredicateData[] {
    let predicatData: PredicateData;
    if (!Array.isArray(xPaths) || !Array.isArray(this.xPathsWithLeafNodes)) {
      return []; // Return an empty array if inputs are invalid
    }

    // Filter `allTheNodes` where `nodeName` exists in `generatedPaths`
    const predicateData = this.xPathsWithLeafNodes
      .filter((node) => xPaths.includes(node.nodeName)) // Filter matching paths
      .map((node) => {
        const parts = node.nodeName.split("/"); // Split the path by '/'
        const tabName = parts[parts.length - 1]; // Get the last segment of the path
        return (predicatData = {
          nodeName: tabName, // Use the last part as the nodeName
          attributes: node.leafNodes, // Keep the original leafNodes
          nodePath: node.nodeName, // Include the full path as nodePath
        });
      });
    return predicateData;
  }

  hasFilters(kvpOfNodeAndFilters: { [key: string]: string | null }[]): boolean {
    let hasFilters = kvpOfNodeAndFilters.some((item: any) => {
      return Object.values(item).some(
        (value: any) => value !== null && value !== ""
      );
    });
    return hasFilters;
  }

  //here we are adding the form group properly based on the filters present
  generateTheDataWhenFilters(
    kvpOfNodeAndFilters: { [key: string]: string | null }[],
    listOfPredicateData: PredicateData[]
  ): PredicateData[] {
    let isSimple: boolean = false;
    let isGroup: boolean = false;
    let isNested: boolean = false;
    let predicateDataWithFormData: PredicateData[] = [];
    let attributes: any[] = [];
    kvpOfNodeAndFilters.forEach((kvp: { [key: string]: string | null }) => {
      const key = Object.keys(kvp)[0];
      const value = kvp[key];
      listOfPredicateData.forEach((predicateData: PredicateData) => {
        if (key === predicateData.nodeName && value !== null && value !== "") {
          isSimple = this.checkForSimpleFilter(value);
          isGroup = this.checkForGroupFilter(value);
          isNested = this.checkForNestedFilter(value);
          let matchingNodes = this.xPathsWithLeafNodes.filter(
            (node: any) => node.nodeName === predicateData.nodePath
          );
          matchingNodes?.length > 0
            ? (attributes = matchingNodes[0].leafNodes)
            : (attributes = []);

          if (isSimple) {
            let formData = this.generateFormDataForSimpleFilter(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          } else if (isGroup) {
            let formData = this.generateFormDataForGroup(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          } else if (isNested) {
            let formData = this.generateFormDataForNestedFilter(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          }
        } else if (
          key === predicateData.nodeName &&
          (value === null || value === "")
        ) {
          let predicateWithFormData: PredicateData = {
            nodeName: predicateData.nodeName,
            nodePath: predicateData.nodePath,
            attributes: predicateData.attributes,
            formGroup: this._fb.group({
              groupArray: this._fb.array([]),
            }),
          };
          predicateDataWithFormData.push(predicateWithFormData);
        }
      });
    });
    return predicateDataWithFormData;
  }
  //here we are just adding the empty form group as the filters are not there and then we will return predicate data
  generateTheDataWhenNoFilters(
    listOfPredicateData: PredicateData[]
  ): PredicateData[] {
    return listOfPredicateData.map((item: any) => ({
      nodeName: item.nodeName,
      nodePath: item.nodePath,
      attributes: item.attributes,
      formGroup: this._fb.group({
        groupArray: this._fb.array([]),
      }),
    }));
  }
  checkForSimpleFilter(filterValue: string): boolean {
    let regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    return regex.test(filterValue);
  }
  checkForGroupFilter(filterValue: string): boolean {
    let regex =
      /^\[\s*\(\s*@\w+\s*[=!<>~]{1,3}\s*'[^']+'\s*(\s*(AND|OR)\s*@\w+\s*[=!<>~]{1,3}\s*'[^']+'\s*)+\)\s*\]$/;
    return regex.test(filterValue);
  }
  checkForNestedFilter(filterValue: string): boolean {
    let regex =
      /^\[\s*\(.*?\)\s+(AND|OR)\s+\(.*?\)(\s+(AND|OR)\s+\(.*?\))*\s*\]$/;
    return regex.test(filterValue);
  }
  generateFormDataForSimpleFilter(
    filterValue: string,
    logicalOperator = this.defaultLogicalOperatorForSimpleFilter
  ): FormGroup {
    const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    const values = filterValue.match(regex);
    const condition = values[2];
    const value = values[3];
    const attribute = values[1];
    if (values) {
      let formGroup = this._fb.group({
        groupArray: this._fb.array([
          this._fb.group({
            logicalOperator: this._fb.control(
              logicalOperator,
              Validators.required
            ),
            conditions: this._fb.array([
              this.createConditionGroup(attribute, condition, value),
            ]),
          }),
        ]),
      });
      return formGroup;
    }
  }
  generateFormDataForGroup(filterValue: string): FormGroup {
    const conditionsAndOperators =
      this.extractConditionsAndOperator(filterValue);
    const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    const multipleFormGroups: FormGroup[] = [];
    if (
      conditionsAndOperators.conditions.length > 1 &&
      (conditionsAndOperators.operator !== null ||
        conditionsAndOperators.operator !== "")
    ) {
      conditionsAndOperators.conditions.forEach((item: string) => {
        const values = item.match(regex);
        const condition = values[2];
        const value = values[3];
        const attribute = values[1];
        let singleFormGroup = this.createConditionGroup(
          attribute,
          condition,
          value
        );
        multipleFormGroups.push(singleFormGroup);
      });
    }

    let formGroup = this._fb.group({
      groupArray: this._fb.array([
        this._fb.group({
          logicalOperator: this._fb.control(
            conditionsAndOperators.operator,
            Validators.required
          ),
          conditions: this._fb.array(multipleFormGroups),
        }),
      ]),
    });
    return formGroup;
  }
  generateFormDataForNestedFilter(filterValue: string): FormGroup {
    //first we want all the groups and operators separated from the nested string, here we will get all the nested groups as array of strings and also operators as array of strings returned by the "extractNestedConditions()" method.
    let groupsAndCondition = this.extractNestedConditions(filterValue);
    const multipleOuterFormGroups: FormGroup[] = [];

    groupsAndCondition.groups.forEach((group: string) => {
      const multipleInnerFormGropus: FormGroup[] = [];
      const conditionsAndOperators = this.extractConditionsAndOperator(group);
      const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
      // here in this below if condition we are checking whether if the incoming conditions has just one or more than one conditons. if the incoming group has more than one condition like "(@Services!='no' AND @Status='valid')" then it will have operator but if it has just one condition like "(@orderno='654321')" then it will not have any operator so we will add the default operator by ourself and that is what is done here.
      if (
        conditionsAndOperators.operator === null ||
        conditionsAndOperators.operator === ""
      ) {
        conditionsAndOperators.operator =
          this.defaultLogicalOperatorForSimpleFilter;
      }

      conditionsAndOperators.conditions.forEach((item: string) => {
        const values = item.match(regex);
        const condition = values[2];
        const value = values[3];
        const attribute = values[1];
        let singleFormGroup = this.createConditionGroup(
          attribute,
          condition,
          value
        );
        multipleInnerFormGropus.push(singleFormGroup);
      });

      let formGroup = this._fb.group({
        logicalOperator: this._fb.control(
          conditionsAndOperators.operator,
          Validators.required
        ),
        conditions: this._fb.array(multipleInnerFormGropus),
      });
      multipleOuterFormGroups.push(formGroup);
    });

    let formGroup = this._fb.group({
      groupArray: this._fb.array(multipleOuterFormGroups),
    });
    return formGroup;
  }
  createConditionGroup(
    attribute: string,
    condition: string,
    value: string
  ): FormGroup {
    return this._fb.group({
      attribute: this._fb.control(attribute, Validators.required),
      condition: this._fb.control(condition, Validators.required),
      value: this._fb.control(value, Validators.required),
    });
  }
  extractNestedConditions(filterValue: string) {
    let nestedGroups: NestedGroups;
    // step 1 - remove the '[' and ']'
    const trimmed = filterValue.trim().slice(1, -1).trim();

    // step 2 - match all the groups with '(' and ')' and extract them
    const groups = trimmed.match(/\(.*?\)/g);

    // step 3 - extracting all the group operators in the same order as they are present
    const operators = trimmed
      .split(/\(.*?\)/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    nestedGroups = { groups: groups, operators: operators };

    return nestedGroups;
  }
  extractConditionsAndOperator(filterValue: string): ConditionGroup {
    let conditionsAndOperators: ConditionGroup;
    // Step 1: Remove brackets
    const cleaned = filterValue.replace(/[\[\]\(\)]/g, "");

    // Step 2: Extract the operator (first match is enough, since they're all same)
    const operatorMatch = cleaned.match(/\b(AND|OR)\b/);
    const operator = operatorMatch ? operatorMatch[0] : null;

    // Step 3: Split the conditions by that operator
    const rawConditions = operator
      ? cleaned.split(new RegExp(`\\s+${operator}\\s+`))
      : [cleaned]; // if no operator found, return the full condition as a single entry

    //we adding the square braces [] to each of the filters
    const conditions = rawConditions.map((cond) => `[${cond.trim()}]`);
    conditionsAndOperators = { conditions: conditions, operator: operator };
    return conditionsAndOperators;
  }
  //#endregion
}
